import React, { useState, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { NodeEditorProps } from './NodeEditor';

export const DescriptionEditor: React.FC<NodeEditorProps> = ({ 
    node, 
    freshNodeData,
    transform, 
    palette, 
    onSave, 
    onCancel,
    nodeScale
}) => {
    const [value, setValue] = useState(freshNodeData.description || "");
    const valueRef = useRef(freshNodeData.description || "");
    const onSaveRef = useRef(onSave);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const isSavedOrCancelled = useRef(false);

    useEffect(() => { valueRef.current = value; }, [value]);
    useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
    useEffect(() => { setValue(freshNodeData.description || ""); }, [freshNodeData.description]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    const scale = transform.k;
    const cx = transform.x + node._cartesian.x * scale;
    const cy = transform.y + node._cartesian.y * scale;
    const yOffset = node._dims.titleHeight / 2;

    const triggerSave = () => {
        if (isSavedOrCancelled.current) return;
        isSavedOrCancelled.current = true;
        onSave(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            triggerSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            isSavedOrCancelled.current = true; 
            onCancel();
        }
    };

    const isDark = palette.type === 'dark';

    return (
       <div className="select-text" 
            style={{
               position: 'absolute',
               left: cx,
               top: cy,
               transform: `translate(-50%, calc(-50% + ${yOffset}px)) scale(${scale * nodeScale})`,
               zIndex: 100,
               pointerEvents: 'auto',
               userSelect: 'text',
               WebkitUserSelect: 'text' as any
           }}>
           <textarea
             ref={inputRef}
             value={value}
             onChange={(e) => setValue(e.target.value)}
             style={{
                 fontSize: `11px`,
                 fontFamily: 'Inter, sans-serif',
                 backgroundColor: isDark ? "#1e293b" : "#ffffff", 
                 color: isDark ? "#e2e8f0" : "#334155",
                 minWidth: (node._dims.contentWidth - 6) + 'px',
                 width: Math.max(node._dims.contentWidth - 6, Math.max(...value.split('\n').map(l => l.length)) * 6 + 8) + 'px',
                 minHeight: (node._dims.descHeight - 4) + 'px',
                 height: Math.max(node._dims.descHeight - 4, value.split('\n').length * 14 + 8) + 'px',
                 resize: 'none',
                 padding: '2px 4px',
                 borderRadius: '4px',
                 textAlign: 'center',
                 border: `2px solid #6366f1`,
                 outline: 'none',
                 lineHeight: '1.2',
                 pointerEvents: 'auto',
                 userSelect: 'text',
                 WebkitUserSelect: 'text' as any
             }}
             className="shadow-xl select-text"
             onKeyDown={handleKeyDown}
             onBlur={triggerSave}
             autoFocus
           />
       </div>
    );
};
